import React from 'react';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip
} from 'recharts';
import {
  CAccordion,
  CAccordionItem,
  CAccordionHeader,
  CAccordionBody
} from '@coreui/react';
import {
  AlertTriangle,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Info,
  Terminal,
  Globe,
  Code
} from 'lucide-react';
import styled from 'styled-components';

// Styled Components
const Container = styled.div`
  padding: 2rem;
  background: transparent;
  min-height: 100vh;
`;

const SEVERITY_COLORS = {
  Critical: '#3d0808',
  High:     '#d32f2f',
  Medium:   '#FFC300',
  Low:      '#10901f',
  Informational: '#3e99f5',
};

const ChartContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin: 2rem 0;
  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  transition: transform 0.2s ease;
  display: flex; 
  flex-direction: column; 
  align-items: center;
  height: 550px;
  width: 100%;
  &:hover { transform: translateY(-4px); }
  h5 {
    margin: 0 0 1rem;
    font-size: 1.25rem;
    color: #2d3748;
    font-weight: 600;
    text-align: center;
    width: 100%;
  }
`;

const ChartLegend = styled.div`
  display: flex; 
  flex-wrap: wrap; 
  justify-content: center;
  gap: 1rem; 
  margin-top: 1rem; 
  width: 100%;
`;

const LegendItem = styled.div`
  display: flex; 
  align-items: center; 
  gap: .5rem;
  font-size: .875rem; 
  color: #4a5568;
  span.color-box { 
    width:12px; 
    height:12px; 
    border-radius:3px; 
  }
`;

const StyledAccordion = styled(CAccordion)`
  margin-top: 2rem;
  background: transparent;
`;

const StyledAccordionItem = styled(CAccordionItem)`
  border: none; 
  margin-bottom: 1rem; 
  border-radius: 8px;
  overflow: hidden; 
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  background: white;
`;

const StyledAccordionHeader = styled(CAccordionHeader)`
  background: ${({ severity }) => SEVERITY_COLORS[severity]};
  color: white !important; 
  border: none; 
  padding: 0; 
  width:100%;
  &:hover { opacity: .95; }
  button {
    color: white !important; 
    background: transparent!important;
    border:none!important; 
    box-shadow:none!important;
    width:100%; 
    text-align:left; 
    padding:1rem;
    display:flex; 
    align-items:center; 
    gap:1rem;
    cursor:pointer;
    &::after { filter: brightness(0) invert(1); }
    &:focus { box-shadow:none!important; }
  }
`;

const StyledAccordionBody = styled(CAccordionBody)`
  background: white; 
  padding: 1.5rem;
`;

const RemediationSection = styled.div`
  background: #f8fafc; 
  border-radius:8px; 
  padding:1.5rem;
  margin-top:1rem;
  h6 {
    color: #0369a1; 
    margin:0 0 1rem; 
    font-weight:600;
    display:flex; 
    align-items:center; 
    gap:.5rem;
  }
  ul {
    margin:0; 
    padding-left:1.5rem;
    li { 
      margin-bottom:.5rem; 
      color:#334155; 
    }
  }
`;

const CodeBlock = styled.pre`
  background:#1a1a1a; 
  color:#00ff00; 
  padding:1rem;
  border-radius:6px; 
  margin:1rem 0;
  font-family:'Courier New', Courier, monospace;
  position:relative; 
  width:100%; 
  max-width:100%;
  overflow-x:auto; 
  white-space:pre-wrap; 
  word-wrap:break-word;
  box-sizing:border-box; 
  table-layout:fixed;
  .terminal-header {
    position:absolute; 
    top:0; 
    left:0; 
    right:0;
    background:#333; 
    padding:.5rem 1rem;
    display:flex; 
    align-items:center; 
    gap:.5rem;
    border-top-left-radius:6px; 
    border-top-right-radius:6px;
    color:white; 
    font-size:.875rem;
  }
  code {
    display:block; 
    padding-top:${props => props.hasHeader ? '2.5rem' : '0'};
    max-width:100%; 
    overflow-wrap:break-word;
    word-break:break-all; 
    font-size:.9rem; 
    line-height:1.5;
  }
`;

const VulnerabilityImage = styled.img`
  max-width:100%; 
  border-radius:6px; 
  margin:1rem 0;
  box-shadow:0 2px 4px rgba(0,0,0,0.1);
`;

const DetailsList = styled.dl`
  display:grid; 
  grid-template-columns:auto 1fr;
  gap:.5rem 1rem; 
  margin:1rem 0;
  background:#f8fafc; 
  padding:1rem; 
  border-radius:8px;
  dt { 
    font-weight:600; 
    color:#4a5568; 
  }
  dd { 
    margin:0; 
    color:#2d3748; 
  }
`;

const SectionTitle = styled.h3`
  color:#1a202c; 
  font-size:1.5rem; 
  font-weight:600;
  margin:2rem 0 1rem; 
  display:flex; 
  align-items:center;
  gap:.75rem;
  svg { 
    width:24px; 
    height:24px; 
  }
`;

// Impact colors
const IMPACT_COLORS = {
  'Reputational Loss': '#3e99f5',
  'Financial Loss':    '#7c3aed',
  'Data Loss':         '#f59e0b'
};

const DynamicWebAccordion = ({ data }) => {
  // order for severity chart
  const severityOrder = ['Critical','High','Medium','Low','Informational'];

  // impact mapping
  const impactMapping = {
    'Sensitive Information Disclosure': 'Data Loss',
    'Missing HTTP Security Headers':   'Reputational Loss',
    'Clickjacking':                     'Reputational Loss',
    'Server Banner Disclosure':         'Reputational Loss',
    'Autocomplete Enabled':             'Data Loss',
    'Insecure HTTP Methods Allowed':    'Data Loss',
    'Outdated JavaScript Library':      'Reputational Loss',
    'Website Running on HTTP':          'Reputational Loss',
    'Forced HTTP Browsing Enabled':     'Reputational Loss',
    'Weak Password Policy':             'Financial Loss',
    'Host Header Injection':            'Reputational Loss',
    'Sensitive Files Disclosure':       'Data Loss',
    'Wordpress Vulnerabilities':        'Financial Loss',
    'Stack Trace':                      'Reputational Loss',
    'Weak Ciphers':                     'Data Loss',
    'Authentication Bypass':            'Financial Loss',
    'Authentication':                   'Financial Loss',
    'Missing HTTPOnly Flag in Cookies': 'Reputational Loss',
    'Missing Secure Flag in Cookies':   'Reputational Loss',
    'Missing SameSite Attribute in Cookies':'Reputational Loss',
    'JavsScript Source Map Detected':   'Data Loss',
    'Client Side Prototype Pollution':  'Reputational Loss',
    'Improper Input Validation':        'Financial Loss',
    'SSL/TLS Not Implemented':          'Reputational Loss',
    'Missing HTTP Strict-Transport-Security (HSTS) Header':'Reputational Loss',
    'Missing X-Content-Type-Options Header':'Reputational Loss',
    'Missing X-Frame-Options Header':   'Reputational Loss',
    'Missing X-XSS-Protection Header':  'Reputational Loss',
    'Missing Content-Security-Policy (CSP) Header':'Reputational Loss',
    'Missing Referrer-Policy Header':   'Reputational Loss',
    'Missing Permissions-Policy Header':'Reputational Loss',
    'Captcha Stored In Source Code':    'Financial Loss',
    'Django Debug Mode Enabled':        'Data Loss',
    'Development Configuration Files':  'Data Loss',
    'Directory Listing':                'Data Loss',
    'Generic Email Address Disclosure': 'Reputational Loss',
    'Microsoft IIS Tilde Directory Enumeration':'Data Loss',
    'Client-Side Storage of Sensitive Information':'Data Loss',
    'Cross-Site Request Forgery (CSRF) Token Missing':'Financial Loss',
    'Cross Site Scripting Via SVG File Upload':'Reputational Loss',
    'Open Redirect':                    'Reputational Loss',
    'Unrestricted File Upload':         'Financial Loss',
    'Cross-Origin Resource Sharing (CORS) Misconfiguration':'Data Loss',
    'Local File Inclusion (LFI)':       'Data Loss',
    'Server Side Template Injection (SSTI)':'Data Loss',
    'OS Command Injection':             'Financial Loss',
    'SQL Injection':                    'Financial Loss',
    'Cross Site Scripting (XSS)':       'Reputational Loss',
    'Logout CSRF':                      'Reputational Loss'
  };

  // only keep true-positive objects
  const filterValidVulnerabilities = (vulnerabilities) =>
    vulnerabilities.filter((vuln) => {
      if (!vuln || typeof vuln !== 'object') return false;
      const isMostlyNull = Object.values(vuln).filter(
        (value) => value && value !== 'no'
      ).length < 3;
      if (isMostlyNull) return false;
      const containsErrors = Object.values(vuln).some(
        (value) =>
          typeof value === 'string' &&
          (value.includes('Connection aborted') ||
           value.includes('Remote end closed'))
      );
      if (containsErrors) return false;
      return true;
    });

  // sort by severityOrder index
  const sortBySeverity = (vulnerabilities) =>
    vulnerabilities.sort(
      (a, b) =>
        severityOrder.indexOf(a.severity) -
        severityOrder.indexOf(b.severity)
    );

  // extract from item.results
  const processedData = data.map((item) => ({
    ...item,
    vulnerabilities: sortBySeverity(
      filterValidVulnerabilities(
        Object.values(item.results || {}).filter(
          (v) => typeof v === 'object' &&
                 v &&
                 'isVulnerable' in v &&
                 v.isVulnerable
        )
      )
    ),
  }));

  // build severity counts
  const severityCount = processedData.reduce((acc, item) => {
    item.vulnerabilities.forEach((vuln) => {
      acc[vuln.severity] = (acc[vuln.severity] || 0) + 1;
    });
    return acc;
  }, {});

  // data for severity pie
  const chartData = severityOrder
    .map((severity) => ({
      name: severity,
      value: severityCount[severity] || 0
    }))
    .filter((d) => d.value > 0);

  // data for impact pie
  const processImpactData = () => {
    const impactCount = {};
    processedData.forEach((item) =>
      item.vulnerabilities.forEach((vuln) => {
        const impact = impactMapping[vuln.name];
        if (impact) {
          impactCount[impact] = (impactCount[impact] || 0) + 1;
        }
      })
    );
    return Object.entries(impactCount).map(([name, value]) => ({
      name,
      value
    }));
  };

  // Customized labels for severity chart (outside placement)
  const renderCustomizedLabel = ({
    cx, cy, midAngle,
    innerRadius, outerRadius,
    percent, index, name, value
  }) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 30;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const isRightSide = x > cx;
    
    return (
      <text
        x={x}
        y={y}
        fill={SEVERITY_COLORS[name]}
        textAnchor={isRightSide ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize={12}
        fontWeight={500}
      >
        {`${name}: ${value}`}
      </text>
    );
  };

  // Customized labels for impact chart (outside placement)
  const renderImpactLabel = ({
    cx, cy, midAngle,
    innerRadius, outerRadius,
    percent, index, name, value
  }) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 30;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const isRightSide = x > cx;
    
    return (
      <text
        x={x}
        y={y}
        fill={IMPACT_COLORS[name]}
        textAnchor={isRightSide ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize={12}
        fontWeight={500}
      >
        {`${name}: ${value}`}
      </text>
    );
  };

  const CustomLegend = ({ payload }) => (
    <ChartLegend>
      {payload.map((entry, i) => (
        <LegendItem key={i}>
          <span
            className="color-box"
            style={{ backgroundColor: entry.color }}
          />
          <span>{entry.value} ({entry.payload.value})</span>
        </LegendItem>
      ))}
    </ChartLegend>
  );

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'Critical': return <AlertTriangle color="white" />;
      case 'High':     return <ShieldAlert   color="white" />;
      case 'Medium':   return <Shield        color="white" />;
      case 'Low':      return <ShieldCheck   color="white" />;
      case 'Informational': return <Info    color="white" />;
      default:         return <Shield        color="white" />;
    }
  };

  // const detectLanguage = (code) => {
  //   if (!code) return 'plaintext';
    
  //   // Check for PHP tags
  //   if (code.includes('<?php') || code.includes('<?=')) return 'php';
    
  //   // Check for Node.js/JavaScript patterns
  //   if (code.includes('const ') || code.includes('require(') || 
  //       code.includes('module.exports') || code.includes('import ')) return 'javascript';
    
  //   // Check for Python/Django/Flask patterns
  //   if (code.includes('def ') || code.includes('from django') || 
  //       code.includes('@app.route') || code.includes('import flask')) return 'python';
    
  //   // Check for ASP.NET patterns
  //   if (code.includes('using ') || code.includes('namespace ') || 
  //       code.includes('public class') || code.includes('[Http')) return 'csharp';
    
  //   // Check for Java/Spring patterns
  //   if (code.includes('@RestController') || code.includes('@GetMapping') || 
  //       code.includes('public static void main')) return 'java';
    
  //   // Check for Ruby/Rails patterns
  //   if (code.includes('def ') || code.includes('class ') || 
  //       code.includes('@') || code.includes('end')) return 'ruby';
    
  //   // Default to plaintext
  //   return 'plaintext';
  // };

  const renderCodeRemediation = (vuln) => {
    // Check if we have either remediation steps or code examples
    const hasRemediation = vuln.remediation && vuln.remediation.trim();
    const hasCodeRemediation = vuln.codeRemediation && vuln.codeRemediation.trim();

    if (!hasRemediation && !hasCodeRemediation) {
      return null;
    }

    // const language = detectLanguage(vuln.codeRemediation);

    return (
      <>
        {hasRemediation && (
          <RemediationSection>
            <h6><Code size={18} />Remediation Steps</h6>
            <ul>
              {vuln.remediation.split('. ')
                .filter(step => step.trim())
                .map((step, i) => <li key={i}>{step.trim()}</li>)}
            </ul>
          </RemediationSection>
        )}

        {hasCodeRemediation && (
          <RemediationSection>
            <h6><Terminal size={18} />Code Implementation Example </h6>
            <CodeBlock hasHeader={true}>
              <div className="terminal-header">
                <Terminal size={14} /><span>Terminal</span>
              </div>
              <code>
                {typeof vuln.codeRemediation === 'object' 
                  ? JSON.stringify(vuln.codeRemediation, null, 2)
                  : vuln.codeRemediation}
              </code>
            </CodeBlock>
          </RemediationSection>
        )}
      </>
    );
  };

  return (
    <Container>
      <ChartContainer>
        <ChartCard>
          <h5>Vulnerability Severity Distribution</h5>
          <PieChart width={450} height={400}>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={70}
              outerRadius={100}
              dataKey="value"
              label={renderCustomizedLabel}
              labelLine={false}
            >
              {chartData.map((entry, i) => (
                <Cell
                  key={`cell-${i}`}
                  fill={SEVERITY_COLORS[entry.name]}
                />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
          <CustomLegend
            payload={chartData.map(item => ({
              value: item.name,
              color: SEVERITY_COLORS[item.name],
              payload: item
            }))}
          />
        </ChartCard>

        <ChartCard>
          <h5>Impact Assessment</h5>
          <PieChart width={500} height={400}>
            <Pie
              data={processImpactData()}
              cx="50%"
              cy="50%"
              innerRadius={70}
              outerRadius={100}
              dataKey="value"
              label={renderImpactLabel}
              labelLine={false}
            >
              {processImpactData().map((entry, i) => (
                <Cell
                  key={`impact-cell-${i}`}
                  fill={IMPACT_COLORS[entry.name]}
                />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
          <CustomLegend
            payload={processImpactData().map(item => ({
              value: item.name,
              color: IMPACT_COLORS[item.name],
              payload: item
            }))}
          />
        </ChartCard>
      </ChartContainer>

      <StyledAccordion alwaysOpen>
        {processedData.map((item, idx) => (
          <div key={idx}>
            <SectionTitle>
              <Globe />
              {item.url || `Vulnerability Report #${idx+1}`}
            </SectionTitle>
            {item.vulnerabilities.map((vuln, i) => (
              <StyledAccordionItem key={`${vuln.name}-${i}`}>
                <StyledAccordionHeader severity={vuln.severity}>
                  <div style={{ display:'flex', gap:'1rem', alignItems:'center' }}>
                    {getSeverityIcon(vuln.severity)}
                    {vuln.name} – {vuln.severity}
                  </div>
                </StyledAccordionHeader>
                <StyledAccordionBody>
                  <DetailsList>
                    <dt>Description</dt>
                    <dd>{vuln.description || 'No description available'}</dd>
                    <dt>OWASP Category</dt>
                    <dd>{vuln.owaspCategory || 'N/A'}</dd>
                    <dt>Impact</dt>
                    <dd>{impactMapping[vuln.name] || 'Unknown'}</dd>
                  </DetailsList>

                  {vuln.poc && (
                    <>
                      <h6><b>Proof of Concept (PoC)</b></h6>
                      <VulnerabilityImage
                        src={`/${vuln.poc}`}
                        alt="PoC"
                      />
                    </>
                  )}

                  {vuln.details && (
                    <>
                      <h6><b>Technical Details</b></h6>
                      <CodeBlock hasHeader={true}>
                        <div className="terminal-header">
                          <Terminal size={14} /><span>Terminal</span>
                        </div>
                        <code>
                          {typeof vuln.details === 'object'
                            ? JSON.stringify(vuln.details, null, 2)
                            : vuln.details}
                        </code>
                      </CodeBlock>
                    </>
                  )}

                  {renderCodeRemediation(vuln)}
                </StyledAccordionBody>
              </StyledAccordionItem>
            ))}
          </div>
        ))}
      </StyledAccordion>
    </Container>
  );
};

export default DynamicWebAccordion;